// 챗봇 목록형 응답 공용 컴팩트 테이블 + 공용 셀 렌더러.
// BotResponseRenderer.js와 ManufacturingTaskComplete.js가 함께 쓴다(순환 import 방지를 위해
// 별도 파일로 분리). 원래 CompactListTable은 "고객/사업명·단계·금액·날짜" 4컬럼 고정이었는데,
// 공구 사용 목록/제조 작업 목록/이탈 리포트 등 컬럼 구성이 다른 응답 타입에도 재사용하기 위해
// columns(배열)를 받는 범용 테이블로 일반화했다.
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

export const openChatUrl = (rawUrl) => {
  if (!rawUrl) return;
  let url = rawUrl;
  if (url.startsWith('/')) {
    url = `${window.location.origin}${url}`;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};

// 목록형 응답 공통 푸터 - listSummary가 있을 때만 쓰인다(없으면 기존 화면 그대로 유지).
// 상단 요약 문구가 이미 건수를 담고 있으므로, 더 볼 게 있을 때만 "전체 N건 보기" 버튼(또는 '총 N건' 텍스트)을 보여주고,
// 후속 질문은 칩으로 보여줘 누르면 그대로 새 메시지로 전송한다.
export const BotListFooter = ({ listSummary, shownCount, onNavigateRoute, onOpenFileSearch, appliedConditions, onFollowUp }) => {
  if (!listSummary) return null;
  const { totalCount, viewAll, followUps } = listSummary;
  const hasMore = Number.isFinite(totalCount) && totalCount > shownCount;
  const canOpenViewAll = Boolean(viewAll?.openFileSearch || viewAll?.route);
  const hasFollowUps = Array.isArray(followUps) && followUps.length > 0;

  if (!hasMore && !hasFollowUps) return null;

  const handleViewAll = () => {
    if (!viewAll) return;
    if (viewAll.openFileSearch) {
      onOpenFileSearch?.(appliedConditions);
      return;
    }
    if (viewAll.route) {
      onNavigateRoute?.(viewAll.route);
    }
  };

  return (
    <Stack spacing={0.75} sx={{ mt: 0.75 }}>
      {hasMore && (
        canOpenViewAll ? (
          <Box sx={{ textAlign: 'right' }}>
            <Button size="small" variant="text" onClick={handleViewAll} sx={{ textTransform: 'none' }}>
              {viewAll.label || `전체 ${totalCount}건 보기`}
            </Button>
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right' }}>
            총 {totalCount}건
          </Typography>
        )
      )}
      {hasFollowUps && (
        <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
          {followUps.map((text, index) => (
            <Chip
              key={index}
              label={text}
              size="small"
              variant="outlined"
              clickable
              onClick={() => onFollowUp?.(text)}
              // 휴대폰에서는 손가락으로 누르기 쉽게 키운다(기본 small 칩은 24px).
              // 칩 라벨은 MUI 기본이 nowrap+ellipsis라 긴 제안 문구가 잘려 보였다 - xs에서는
              // 폭을 두고 줄바꿈하게 푼다(2026-10-15).
              sx={{
                fontSize: '0.72rem', cursor: 'pointer', height: { xs: 'auto', sm: 24 },
                maxWidth: { xs: 260, sm: 'none' },
                '& .MuiChip-label': {
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  display: 'block',
                  py: { xs: 0.5, sm: 0 },
                  wordBreak: 'keep-all',
                },
              }}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

// 금액 표시: 원 단위 raw 값을 만원으로 변환한다("1,450만"). 1만원 미만이면 원 그대로,
// 값이 없거나 0 이하면 '-'. 백엔드 BotListSummarySupport.formatWon과 같은 기준.
export const formatAmountMan = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return '-';
  if (num < 10000) return `${num.toLocaleString()}원`;
  return `${Math.round(num / 10000).toLocaleString()}만`;
};

// 날짜 표시: "YYYY-MM-DD(...)" 형태를 "MM-DD"로 축약한다. 파싱 실패/값 없음은 '-'.
export const formatMonthDay = (dateStr) => {
  if (!dateStr) return '-';
  const matched = `${dateStr}`.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (matched) return `${matched[2]}-${matched[3]}`;
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return '-';
  return `${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
};

// 공용 셀 렌더러들 - "핵심 정보 먼저, ID/코드는 작게" 원칙을 테이블 셀에서 재사용하기 위한 조각.
// 제목(굵게, 1줄 말줄임) + 부제(작게, 회색, 1줄 말줄임) 2행 셀.
export const renderTitleCell = (title, subtitle, fallback = '-') => (
  <>
    <Tooltip title={title || ''}>
      <Typography variant="body2" noWrap sx={{ fontWeight: 'bold' }}>
        {title || fallback}
      </Typography>
    </Tooltip>
    {subtitle && (
      <Tooltip title={subtitle}>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {subtitle}
        </Typography>
      </Tooltip>
    )}
  </>
);

// 단계/상태/카테고리 칩 셀. color는 상태(성공/주의/실패)일 때만 success/warning/error를 넘기고,
// 카테고리·단계 구분일 때는 'default'(중립)를 넘긴다.
export const renderStageCell = (label, color = 'default', variant = 'outlined') => (
  label ? (
    <Chip
      label={label}
      size="small"
      variant={variant}
      color={color}
      sx={{ height: 22, maxWidth: '100%', '& .MuiChip-label': { px: 0.75, fontSize: '0.68rem', overflow: 'hidden', textOverflow: 'ellipsis' } }}
    />
  ) : (
    <Typography variant="caption" color="text.secondary">-</Typography>
  )
);

// 금액(만원 단위) 셀 - 우측 정렬 굵은 글씨.
export const renderAmountCell = (value) => (
  <Typography variant="body2" sx={{ fontWeight: 'bold' }} noWrap>
    {formatAmountMan(value)}
  </Typography>
);

// 날짜(MM-DD) 셀 - 우측 정렬 회색.
export const renderDateCell = (value) => (
  <Typography variant="body2" color="text.secondary" noWrap>
    {formatMonthDay(value)}
  </Typography>
);

/**
 * 목록형 챗봇 응답 공용 컴팩트 테이블 ("B안"). columns로 컬럼 구성을 자유롭게 바꿀 수 있다.
 * - columns: [{ key, header, width, align, render(item, index) }]
 * - getActionUrl(item): 있으면 행 전체가 클릭 가능 - openChatUrl로 새 탭을 연다(기존 동작).
 * - onRowClick(item): getActionUrl 대신 임의 동작(예: 다이얼로그 열기)이 필요할 때 사용.
 * - getExpandedContent(item): 있으면 행을 눌러 바로 아래에 상세 내용을 펼쳐 보여준다(정보 손실 없이
 *   테이블을 컴팩트하게 유지하기 위한 보조 수단 - 예: 공구 실적 비교의 편차 문구/실적 공구번호 목록).
 */
export const CompactListTable = ({
  items,
  columns,
  getRowKey,
  getActionUrl,
  onRowClick,
  getExpandedContent,
}) => {
  const [expandedKey, setExpandedKey] = useState(null);
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));

  if (!items || items.length === 0 || !Array.isArray(columns) || columns.length === 0) return null;

  const isClickable = (item) => Boolean(getExpandedContent) || Boolean(onRowClick) || Boolean(getActionUrl?.(item));

  const handleRowClick = (item, key) => {
    if (getExpandedContent) {
      setExpandedKey((prev) => (prev === key ? null : key));
      return;
    }
    if (onRowClick) {
      onRowClick(item);
      return;
    }
    const url = getActionUrl?.(item);
    if (url) openChatUrl(url);
  };

  // 휴대폰 폭에서는 4칸 표가 칸마다 "요…", "1…"처럼 잘려서 읽을 수 없었다(2026-10-06 모바일 스크린샷).
  // 좁은 화면에서는 행마다 2줄 카드로 바꾼다: 첫 줄은 제목 칸(썸네일이 있으면 썸네일+제목),
  // 둘째 줄은 나머지 칸(단계·금액·날짜 등)을 한 줄에 이어 놓고, 오른쪽 정렬 칸은 오른쪽으로 민다.
  if (isNarrow) {
    const leadCount = columns[0]?.key === 'thumb' && columns.length > 1 ? 2 : 1;
    const leadColumns = columns.slice(0, leadCount);
    const restColumns = columns.slice(leadCount);
    const firstRightIndex = restColumns.findIndex((col) => col.align === 'right');
    return (
      <Box sx={{ border: '1px solid #dfe3e8', borderRadius: 1 }}>
        {items.map((item, index) => {
          const key = getRowKey ? getRowKey(item, index) : index;
          const clickable = isClickable(item);
          const expanded = Boolean(getExpandedContent) && expandedKey === key;
          return (
            <Box
              key={key}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={clickable ? () => handleRowClick(item, key) : undefined}
              onKeyDown={clickable ? (event) => {
                if (event.key === 'Enter') handleRowClick(item, key);
              } : undefined}
              sx={{
                px: 1,
                py: 0.75,
                cursor: clickable ? 'pointer' : 'default',
                borderTop: index === 0 ? 0 : '1px solid #eef0f2',
                '&:hover': clickable ? { backgroundColor: '#f8f9fa' } : undefined,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                {leadColumns.map((col, colIndex) => (
                  <Box key={col.key} sx={{ minWidth: 0, flex: colIndex === leadColumns.length - 1 ? 1 : 'none' }}>
                    {col.render(item, index)}
                  </Box>
                ))}
              </Stack>
              {restColumns.length > 0 && (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {restColumns.map((col, colIndex) => (
                    <Box key={col.key} sx={{ minWidth: 0, maxWidth: '100%', ml: colIndex === firstRightIndex ? 'auto' : 0 }}>
                      {col.render(item, index)}
                    </Box>
                  ))}
                </Stack>
              )}
              {expanded && (
                <Box sx={{ mt: 0.75, p: 1, backgroundColor: '#fafafa', borderRadius: 1 }} onClick={(event) => event.stopPropagation()}>
                  {getExpandedContent(item)}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto', border: '1px solid #dfe3e8', borderRadius: 1 }}>
      <Table size="small" sx={{ tableLayout: 'fixed', '& .MuiTableCell-root': { px: 1, py: 0.5 } }}>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.key} align={col.align} sx={{ width: col.width }}>
                {col.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, index) => {
            const key = getRowKey ? getRowKey(item, index) : index;
            const clickable = isClickable(item);
            const expanded = Boolean(getExpandedContent) && expandedKey === key;
            return (
              <React.Fragment key={key}>
                <TableRow
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onClick={clickable ? () => handleRowClick(item, key) : undefined}
                  onKeyDown={clickable ? (event) => {
                    if (event.key === 'Enter') handleRowClick(item, key);
                  } : undefined}
                  sx={{
                    cursor: clickable ? 'pointer' : 'default',
                    '&:hover': clickable ? { backgroundColor: '#f8f9fa' } : undefined,
                    ...(!expanded ? { '&:last-child td': { borderBottom: 0 } } : null),
                  }}
                >
                  {columns.map((col) => (
                    <TableCell key={col.key} align={col.align}>{col.render(item, index)}</TableCell>
                  ))}
                </TableRow>
                {expanded && (
                  <TableRow sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell colSpan={columns.length} sx={{ backgroundColor: '#fafafa' }}>
                      {getExpandedContent(item)}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};
